import * as Yup from 'yup';
import _ from 'lodash';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { EmailValidator } from 'commons-validator-js';

import { translations } from 'src/shared/translation';
import countries from 'src/shared/helpers/countries.json';
import { AppConfigContextValue } from 'src/shared/contexts/app-config';

import { EMPTY_OBJECT } from '../../../../shared/helpers/object/empty-object';

const transl = translations.nearmecheck.start_page.form;

export const inputsIdsMap = {
    'manual-form-category-select': 'categoryId',
    'manual-form-country-select': 'country',
    'manual-form-company-name': 'name',
    'manual-form-street-and-number': 'streetAndNumber',
    'manual-form-zip-code': 'zipCode',
    'manual-form-city': 'city',
    'manual-form-phone-number': 'phoneNumber',
    'manual-form-email': 'email',
};

const supportedQueryArgs = Object.values(inputsIdsMap);

export const currentQueryArgs = (locationSearch: string): { [k: string]: string } => {
    const search = new URLSearchParams(locationSearch);
    return supportedQueryArgs
        .filter((queryArg) => search.has(queryArg))
        .reduce((prev, current) => ({ ...prev, [current]: search.get(current) }), EMPTY_OBJECT);
};

export const getSchema = (
    t: Function,
    emailFieldVisibility: AppConfigContextValue['emailFieldVisibility'],
    phoneNumberFieldVisibility: AppConfigContextValue['phoneNumberFieldVisibility']
) => {
    const createErrorMsg = (fieldName: string) => {
        return t(transl.error.field_required, {
            fieldName: t(fieldName),
        });
    };

    const validateParam = (param: AppConfigContextValue['emailFieldVisibility']) => {
        if (param === 'hidden') return false;
        return true;
    };

    const createPhoneValidator = (
        t: Function,
        param: AppConfigContextValue['phoneNumberFieldVisibility']
    ) => {
        const testPhoneNumber = (value?: string, context?: Yup.TestContext) => {
            //  * validator function returns true if the field is valid and error or false when the field is not valid

            // ignore validation if field is hidden
            if (!validateParam(param)) return true;

            // valid if the field is optional and no value
            if (param === 'optional' && !value) return true;

            const regionCode = context?.parent['manual-form-country-select'];

            // not valid if theres no value and country
            if (!value || !regionCode) return false;

            return parsePhoneNumber(value, { regionCode }).valid;
        };

        return Yup.string().test('is-valid-phone', t(transl.phone_number.error), testPhoneNumber);
    };

    const createEmailValidator = (
        t: Function,
        param: AppConfigContextValue['emailFieldVisibility']
    ) => {
        const testEmail = (value?: string) => {
            // ignore validation if param is not valid
            if (!validateParam(param)) return true;

            // valid if the field is optional and no value
            if (param === 'optional' && !value) return true;

            if (!value) return false;

            return new EmailValidator().isValid(value);
        };
        return Yup.string().test('is-valid-email', t(transl.email.error), testEmail);
    };

    return Yup.object().shape({
        'manual-form-country-select': Yup.string().required(createErrorMsg(transl.country.label)),
        'manual-form-category-select': Yup.string().required(createErrorMsg(transl.category.label)),
        'manual-form-company-name': Yup.string().required(
            createErrorMsg(transl.company_name.label)
        ),
        'manual-form-street-and-number': Yup.string().required(
            createErrorMsg(transl.address.label)
        ),
        'manual-form-zip-code': Yup.string().when('manual-form-country-select', {
            is: (val: keyof typeof countries) => !val || countries[val]?.usesZipCode,
            then: Yup.string().required(createErrorMsg(transl.zip_code.label)),
            otherwise: Yup.string(),
        }),
        'manual-form-city': Yup.string().when('manual-form-country-select', {
            is: (val: keyof typeof countries) => countries[val]?.usesZipCode,
            then: Yup.string(),
            otherwise: Yup.string().required(createErrorMsg(transl.city.label)),
        }),
        'manual-form-phone-number': createPhoneValidator(t, phoneNumberFieldVisibility),
        'manual-form-email': createEmailValidator(t, emailFieldVisibility),
    });
};

export const getInitialValuesFromUrl = (queryMap: { [k: string]: string }) => {
    const queryKeys = Object.keys(queryMap);
    if (!queryKeys.length) {
        return undefined;
    }

    return _.omitBy(
        {
            'manual-form-country-select': queryMap.country,
            'manual-form-category-select': queryMap.categoryId,
            'manual-form-company-name': queryMap.name,
            'manual-form-street-and-number': queryMap.streetAndNumber,
            'manual-form-zip-code': queryMap.zipCode,
            'manual-form-city': queryMap.city,
        },
        _.isNil
    );
};
